import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Separator } from "@/components/ui/separator"
import { useAdminAuth } from "@/context/Admin"
import Loader from "@/components/loader/loader"
import { toast } from "sonner"

export default function SystemSettingsTab() {
  const {
    getAllSystemSettings,
    updateSystemSetting,
    enableDbBackup,
    disableDbBackup,
    enableSentry,
    disableSentry,
  } = useAdminAuth();
  
  const [loading, setLoading] = useState(true);
  const [dbBackupEnabled, setDbBackupEnabled] = useState(false);
  const [sentryEnabled, setSentryEnabled] = useState(false);
  const [updating, setUpdating] = useState({});

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const settings = await getAllSystemSettings();
        setDbBackupEnabled(settings?.db_backup_enabled?.value === true || settings?.db_backup_enabled?.value === 'true');
        setSentryEnabled(settings?.sentry_enabled?.value === true || settings?.sentry_enabled?.value === 'true');
      } catch (error) {
        console.error("Error fetching system settings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [getAllSystemSettings]);

  const handleDbBackupToggle = async (enabled) => {
    setUpdating({ ...updating, dbBackup: true });
    try {
      if (enabled) {
        await enableDbBackup();
        setDbBackupEnabled(true);
      } else {
        await disableDbBackup();
        setDbBackupEnabled(false);
      }
    } catch (error) {
      console.error("Error toggling database backup:", error);
    } finally {
      setUpdating({ ...updating, dbBackup: false });
    }
  };

  const handleSentryToggle = async (enabled) => {
    setUpdating({ ...updating, sentry: true });
    try {
      if (enabled) {
        await enableSentry();
        setSentryEnabled(true);
      } else {
        await disableSentry();
        setSentryEnabled(false);
      }
    } catch (error) {
      console.error("Error toggling Sentry:", error);
    } finally {
      setUpdating({ ...updating, sentry: false });
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-primary">Data Management</CardTitle>
          <CardDescription>Manage database backups and data retention policies.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-primary">Automated Backups</p>
              <p className="text-sm text-gray-600">Enable daily automated database backups.</p>
            </div>
            <Switch 
              checked={dbBackupEnabled} 
              onCheckedChange={handleDbBackupToggle}
              disabled={updating.dbBackup}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-primary">Data Retention Policy</p>
              <p className="text-sm text-gray-600">
                Automatically delete old logs and inactive user data after 90 days.
              </p>
            </div>
            <Switch disabled />
            <p className="text-xs text-gray-500 ml-2">Not available</p>
          </div>
          <Button className="bg-primary hover:bg-primary/90 text-white" disabled>
            Run Manual Backup
          </Button>
        </CardContent>
      </Card>
      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="text-primary">Logging & Monitoring</CardTitle>
          <CardDescription>Configure system logging levels and error reporting.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-primary">Enable Detailed Logging</p>
              <p className="text-sm text-gray-600">
                Record more detailed system events for debugging and auditing.
              </p>
            </div>
            <Switch disabled />
            <p className="text-xs text-gray-500 ml-2">Not available</p>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-primary">Error Reporting (Sentry)</p>
              <p className="text-sm text-gray-600">Automatically send error reports to developers.</p>
            </div>
            <Switch 
              checked={sentryEnabled} 
              onCheckedChange={handleSentryToggle}
              disabled={updating.sentry}
            />
          </div>
          <Button variant="outline" disabled>
            View System Logs
          </Button>
        </CardContent>
      </Card>
    </>
  )
}
